import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Loader2, Store, MapPin, ExternalLink } from "lucide-react";

// Fix default marker icon (Webpack/CRA issue)
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

// City centers (fallback when biz has no coords)
const CITY_CENTERS = {
  Tijuana: [32.5149, -117.0382],
  Rosarito: [32.3614, -117.0539],
  Ensenada: [31.8667, -116.5963],
  Mexicali: [32.6519, -115.4683],
  "San Diego": [32.7157, -117.1611],
};

export default function GuiaMapPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState("Todas");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCity !== "Todas") params.city = selectedCity;
        const { data } = await api.get("/guia/map", { params });
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedCity]);

  // Center the map: use selected city center, or first item with coords, else Tijuana
  const center = useMemo(() => {
    if (selectedCity !== "Todas" && CITY_CENTERS[selectedCity]) return CITY_CENTERS[selectedCity];
    const withCoords = items.find((b) => b.lat && b.lng);
    if (withCoords) return [withCoords.lat, withCoords.lng];
    return CITY_CENTERS.Tijuana;
  }, [items, selectedCity]);

  // Project items without coords onto their city center with small jitter
  const itemsWithCoords = useMemo(() => {
    return items
      .map((b, idx) => {
        if (b.lat && b.lng) return b;
        const cityCenter = CITY_CENTERS[b.city];
        if (!cityCenter) return null;
        // Deterministic jitter so markers don't all stack
        const angle = (idx * 47) % 360;
        const radius = 0.005 + ((idx * 13) % 50) / 10000;
        const lat = cityCenter[0] + Math.cos((angle * Math.PI) / 180) * radius;
        const lng = cityCenter[1] + Math.sin((angle * Math.PI) / 180) * radius;
        return { ...b, lat, lng, _approx: true };
      })
      .filter(Boolean);
  }, [items]);

  const cities = ["Todas", ...Object.keys(CITY_CENTERS)];

  return (
    <div className="min-h-screen bg-sand pb-12" data-testid="guia-map-page">
      <div className="container-main py-6">
        <Link
          to="/guia"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="map-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la Guía
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-agave">Mapa de la Guía</h1>
              <p className="text-sm text-neutral-600">
                {loading ? "Cargando…" : `${itemsWithCoords.length} negocios en el mapa`}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {cities.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCity(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  selectedCity === c
                    ? "bg-terracotta text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-100 border border-neutral-200"
                }`}
                data-testid={`map-city-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="card-base p-0 overflow-hidden" style={{ height: "70vh", minHeight: 500 }}>
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
            </div>
          ) : (
            <MapContainer
              center={center}
              zoom={12}
              style={{ height: "100%", width: "100%" }}
              data-testid="guia-leaflet-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {itemsWithCoords.map((b) => (
                <Marker key={b.business_id} position={[b.lat, b.lng]}>
                  <Popup>
                    <div className="space-y-1" data-testid={`map-popup-${b.business_id}`}>
                      <div className="flex items-center gap-2">
                        {b.logo_url ? (
                          <img src={b.logo_url} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <Store className="w-6 h-6 text-neutral-400" />
                        )}
                        <strong className="text-sm">{b.name}</strong>
                      </div>
                      <p className="text-xs text-neutral-600">{b.address || b.city}</p>
                      {b.discount_percent ? (
                        <p className="text-xs font-bold text-terracotta">
                          -{Math.round(b.discount_percent)}% para miembros
                        </p>
                      ) : null}
                      {b._approx && (
                        <p className="text-[10px] italic text-neutral-400">Ubicación aproximada</p>
                      )}
                      <div className="flex gap-2 mt-1">
                        <Link
                          to={`/guia/${b.business_id}`}
                          className="text-xs text-terracotta font-medium hover:underline"
                        >
                          Ver detalle →
                        </Link>
                        {b.maps_link && (
                          <a
                            href={b.maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5"
                          >
                            Maps <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
}
